import { cache } from 'react';
import { getPageConfig } from '@/lib/aggregation';
import type { LogOutPopData, ZonesTypes } from '@/types/aggregation/common';
import type { PrismicObject } from '@/types/prismic/common';
import { isNativeApiAllowedRoute } from './app-data-route';
import { resolveAppData } from './resolve-app-data';

export type ProductContext = {
    bonusDraw: string;
    themeName: string;
    productFeatures: string[];
    rewardsMachineContent: unknown;
    region: string;
    zones: ZonesTypes[];
    logoutPopup: PrismicObject<'logout_popup', LogOutPopData> | undefined;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const toFeatureNames = (features: unknown): string[] => {
    if (!Array.isArray(features)) {
        return [];
    }

    return features
        .map((feature) => {
            if (typeof feature === 'string') {
                return feature;
            }

            return isRecord(feature) && typeof feature.feature_name === 'string' ? feature.feature_name : null;
        })
        .filter((feature): feature is string => feature !== null);
};

const toLogoutPopupDocument = (logoutPopup: unknown): PrismicObject<'logout_popup', LogOutPopData> | undefined => {
    if (!logoutPopup || !isRecord(logoutPopup)) {
        return undefined;
    }

    if ('data' in logoutPopup && isRecord(logoutPopup.data)) {
        return logoutPopup as PrismicObject<'logout_popup', LogOutPopData>;
    }

    return {
        type: 'logout_popup',
        data: logoutPopup as LogOutPopData
    } as PrismicObject<'logout_popup', LogOutPopData>;
};

const fromPageConfig = async (currentRoute: string): Promise<ProductContext | null> => {
    const pageConfig = await getPageConfig(currentRoute);
    const productData = pageConfig?.pageConfig?.product?.data;

    if (!pageConfig || !productData) {
        return null;
    }

    return {
        bonusDraw: productData.bonus_draw ?? '',
        themeName: productData.theme_name ?? 'default',
        productFeatures: toFeatureNames(productData.features),
        rewardsMachineContent: pageConfig.pageConfig?.rewards_machine_content,
        region: pageConfig.regionInfo?.currentRegion?.region ?? '',
        zones: pageConfig.pageConfig?.content?.data?.zones ?? [],
        logoutPopup: toLogoutPopupDocument(productData.logout_popup)
    };
};

export const getProductContext = cache(async (currentRoute = '/'): Promise<ProductContext | null> => {
    if (isNativeApiAllowedRoute(currentRoute)) {
        const appData = await resolveAppData(currentRoute);
        if (appData) {
            return {
                bonusDraw: appData.shell.bonusDraw,
                themeName: appData.shell.themeName,
                productFeatures: appData.shell.productFeatures,
                rewardsMachineContent: appData.shell.rewardsMachineContent,
                region: appData.regionInfo?.currentRegion?.region ?? '',
                zones: appData.page.zones,
                logoutPopup: toLogoutPopupDocument(appData.shell.logoutPopup)
            };
        }
    }

    return fromPageConfig(currentRoute);
});
